import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, withLatestFrom } from 'rxjs/operators';

import {
  LoadCertificateReport,
  LoadCertificateReportSuccess,
  LoadCheckSheetAssignmentForReportByProject,
  LoadCheckSheetAssignmentForReportByTask,
  LoadCheckSheetAssignmentForReportSuccess,
  LoadPunchesForBurnDownSuccess,
  LoadPunchesForReportByProject,
  LoadPunchesForReportByTask,
  ReportsActionTypes,
} from '@completion/actions';
import { State } from '@completion/reducers';
import { getCurrentTask, getCurrentProject } from '@completion/selectors';
import { NotificationService } from '../../services/notification.service';
import { ReportService } from '../../services/report.service';
import { Project } from '@completion/models';
import { trackResource } from './resource-fetch.util';

@Injectable()
export class ReportEffect {
  constructor(
    private readonly actions$: Actions,
    private readonly reportService: ReportService,
    private readonly store: Store<State>,
    private readonly toastr: NotificationService,
  ) {}

  loadReportPunchesByTask$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadPunchesForReportByTask>(ReportsActionTypes.LoadPunchesForReportByTask),
      withLatestFrom(this.store.select(getCurrentProject), this.store.select(getCurrentTask)),
      filter(([_, currentProject, task]) => !!(currentProject && task)),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.reportService,
        method: 'getProjectPunchesByTask',
        serviceArgs: ([_, currentProject, task]) => [currentProject.id, (task as any).id],
        onSuccess: (response) => new LoadPunchesForBurnDownSuccess(response as any),
      }),
    ),
  );

  loadReportPunchesByProject$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadPunchesForReportByProject>(ReportsActionTypes.LoadPunchesForReportByProject),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      filter(([_, currentProject]) => !!currentProject),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.reportService,
        method: 'getProjectPunches',
        serviceArgs: ([_, currentProject]) => [currentProject.id],
        onSuccess: (response) => new LoadPunchesForBurnDownSuccess(response as any),
      }),
    ),
  );

  loadCheckSheetAssignmentByTask$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadCheckSheetAssignmentForReportByTask>(ReportsActionTypes.LoadCheckSheetAssignmentForReportByTask),
      withLatestFrom(this.store.select(getCurrentProject), this.store.select(getCurrentTask)),
      filter(([_, currentProject, task]) => !!(currentProject && task)),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.reportService,
        method: 'getCheckSheetAssignmentByTask',
        serviceArgs: ([_, currentProject, task]) => [currentProject.id, (task as any).id],
        onSuccess: (response) => new LoadCheckSheetAssignmentForReportSuccess(response as any),
      }),
    ),
  );

  loadCheckSheetAssignmentByProject$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadCheckSheetAssignmentForReportByProject>(ReportsActionTypes.LoadCheckSheetAssignmentForReportByProject),
      withLatestFrom(this.store.select(getCurrentProject)),
      filter(([_, currentProject]) => !!currentProject),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.reportService,
        method: 'getCheckSheetAssignmentByProject',
        serviceArgs: ([_, currentProject]) => [currentProject.id],
        onSuccess: (response) => new LoadCheckSheetAssignmentForReportSuccess(response as any),
      }),
    ),
  );

  loadCertificateReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadCertificateReport>(ReportsActionTypes.LoadCertificateReport),
      withLatestFrom(this.store.select(getCurrentProject)),
      filter(([_, currentProject]) => !!currentProject),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.reportService,
        method: 'getCertificateReport',
        serviceArgs: ([_, currentProject]) => [currentProject.id],
        onSuccess: (response) => new LoadCertificateReportSuccess(response as any),
      }),
    ),
  );
}
